import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { fetchSpots, fetchSpotReservations, createReservation, fetchMyReservations, fetchHouseholdNightUsage } from '../api';

// Reservations UI — availability + booking + a member's own reservations.
//
// Flow: load spots -> pick one -> see the dates it's already booked -> submit
// dates. The booking form surfaces the API's domain errors verbatim, so QA can
// observe double-booking prevention (409 date_conflict) and bad-date rejection
// (400 invalid_dates). A "Your reservations" panel shows the signed-in member's
// own bookings with full spot detail, split into upcoming and past.
//
// Only rendered for linked members (gated by Profile), so every call carries a
// resolvable member principal.

// SQL `date` columns come back as ISO strings (e.g. "2026-07-28T00:00:00.000Z").
// Parse the date-only portion so we never shift a day across the viewer's TZ.
function fmtDate(iso) {
  if (!iso) return '';
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return datePart;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// Whole nights between two date-only ISO strings (checkout - checkin).
function nightsBetween(startIso, endIso) {
  const a = String(startIso).slice(0, 10);
  const b = String(endIso).slice(0, 10);
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  if (!ay || !by) return 0;
  const ms = new Date(by, bm - 1, bd) - new Date(ay, am - 1, ad);
  return Math.max(0, Math.round(ms / 86400000));
}

// A date-only ISO is in the past once its checkout day is before today.
function isPast(endIso) {
  const b = String(endIso).slice(0, 10);
  const [y, m, d] = b.split('-').map(Number);
  const today = new Date();
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return new Date(y, m - 1, d) < todayMid;
}

// Parse a date-only ISO string into a LOCAL Date at midnight (no TZ shift).
function isoToLocalDate(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// Format a local Date back to a date-only ISO string (yyyy-mm-dd) for the API.
function toISODate(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

const money = (cents) => `$${(cents / 100).toFixed(2)}`;
const SPOT_ICON = { cabin: '🏡', rv: '🚐', campsite: '⛺' };
const spotIcon = (t) => SPOT_ICON[String(t || '').toLowerCase()] || '📍';

export default function Reservations() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  // getActiveAccount() returns a fresh object each render; depending on it in
  // effects re-fires the fetch every render (request storm). Key off a stable id.
  const accountKey = account?.homeAccountId || account?.username || null;

  const [spots, setSpots] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [reservations, setReservations] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(true);
  const [loadingRes, setLoadingRes] = useState(false);
  const [form, setForm] = useState({ startDate: '', endDate: '' });
  const [range, setRange] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { kind: 'ok'|'err', message }
  const [error, setError] = useState(null);

  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);
  const [nightUsage, setNightUsage] = useState(null);

  // Load spots once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ok, status, data } = await fetchSpots(instance, account);
        if (cancelled) return;
        if (!ok) { setError(`Couldn't load spots (${status})`); setLoadingSpots(false); return; }
        setSpots(data.spots || []);
        setLoadingSpots(false);
      } catch (e) {
        if (!cancelled) { setError(e.message); setLoadingSpots(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  // Load the member's own reservations once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ok, data } = await fetchMyReservations(instance, account);
        if (cancelled) return;
        setMine(ok ? (data.reservations || []) : []);
        setLoadingMine(false);
      } catch {
        if (!cancelled) { setMine([]); setLoadingMine(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  // Load household cabin-night usage once (null if not in a household).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ok, data } = await fetchHouseholdNightUsage(instance, account);
        if (cancelled) return;
        setNightUsage(ok ? (data.usage || null) : null);
      } catch {
        if (!cancelled) setNightUsage(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  // Reload a spot's reservations when the selection changes.
  useEffect(() => {
    // Clear any in-progress date pick when switching spots — availability differs.
    setRange(undefined);
    setForm({ startDate: '', endDate: '' });
    setFeedback(null);
    if (!selectedId) { setReservations([]); return; }
    let cancelled = false;
    setLoadingRes(true);
    (async () => {
      try {
        const { ok, data } = await fetchSpotReservations(instance, account, selectedId);
        if (cancelled) return;
        setReservations(ok ? (data.reservations || []) : []);
        setBlocks(ok ? (data.blocks || []) : []);
        setLoadingRes(false);
      } catch {
        if (!cancelled) { setReservations([]); setBlocks([]); setLoadingRes(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, accountKey]);

  async function refreshAfterBooking() {
    const [spotRes, myRes] = await Promise.all([
      fetchSpotReservations(instance, account, selectedId),
      fetchMyReservations(instance, account),
    ]);
    if (spotRes.ok) { setReservations(spotRes.data.reservations || []); setBlocks(spotRes.data.blocks || []); }
    if (myRes.ok) setMine(myRes.data.reservations || []);
    try {
      const usageRes = await fetchHouseholdNightUsage(instance, account);
      if (usageRes.ok) setNightUsage(usageRes.data.usage || null);
    } catch { /* non-fatal */ }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFeedback(null);
    if (!selectedId) { setFeedback({ kind: 'err', message: 'Pick a spot first.' }); return; }
    if (!form.startDate || !form.endDate) { setFeedback({ kind: 'err', message: 'Choose both dates.' }); return; }

    setSubmitting(true);
    try {
      const { ok, data } = await createReservation(instance, account, {
        spotId: selectedId,
        startDate: form.startDate,
        endDate: form.endDate,
      });
      if (ok) {
        setFeedback({ kind: 'ok', message: 'Booked! Your reservation is confirmed.' });
        setForm({ startDate: '', endDate: '' });
        setRange(undefined);
        await refreshAfterBooking();
      } else {
        // Surface the API's domain message (e.g. date_conflict, invalid_dates).
        setFeedback({ kind: 'err', message: data?.message || 'Booking failed.' });
      }
    } catch (err) {
      setFeedback({ kind: 'err', message: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingSpots) return <div className="card">Loading spots…</div>;
  if (error) return <div className="card error"><h2>Reservations unavailable</h2><p>{error}</p></div>;

  const selectedSpot = spots.find((s) => s.id === selectedId);
  // Only upcoming bookings matter for availability; hide anything already ended.
  const upcomingBookings = reservations.filter((r) => !isPast(r.end_date));

  // Build calendar matchers from booked ranges. A reservation start->end means
  // nights start..(end-1) are occupied; the checkout day itself is free for a
  // new check-in, so we disable start..end-1 (inclusive).
  const bookedRanges = upcomingBookings
    .map((r) => {
      const from = isoToLocalDate(r.start_date);
      const checkout = isoToLocalDate(r.end_date);
      if (!from || !checkout) return null;
      return { from, to: addDays(checkout, -1) };
    })
    .filter(Boolean);

  // Admin blocks (maintenance/events) make a spot unavailable too. Same half-open
  // interval: block start..end means nights start..(end-1) are held.
  const upcomingBlocks = (blocks || []).filter((b) => !isPast(b.end_date));
  const blockedRanges = upcomingBlocks
    .map((b) => {
      const from = isoToLocalDate(b.start_date);
      const to = isoToLocalDate(b.end_date);
      if (!from || !to) return null;
      return { from, to: addDays(to, -1) };
    })
    .filter(Boolean);

  const todayMid = (() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate()); })();
  // Disable past days, booked nights, AND blocked nights. excludeDisabled stops a
  // selected range from spanning any of them.
  const disabledDays = [{ before: todayMid }, ...bookedRanges, ...blockedRanges];

  function handleRangeSelect(next) {
    setRange(next);
    setForm({ startDate: toISODate(next?.from), endDate: toISODate(next?.to) });
    if (feedback) setFeedback(null);
  }

  const myUpcoming = mine.filter((r) => !isPast(r.end_date));
  const myPast = mine.filter((r) => isPast(r.end_date));

  return (
    <>
      {nightUsage && <CabinNightsBanner usage={nightUsage} />}

      <div className="card">
        <h2>Reserve a spot</h2>

        {spots.length === 0 ? (
          <p className="muted">No bookable spots yet. An admin can add cabins and campsites.</p>
        ) : (
          <>
            <label className="field">
              <span>Spot</span>
              <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                <option value="">— choose a spot —</option>
                {spots.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.spot_type}) — {money(s.nightly_cents)}/night
                  </option>
                ))}
              </select>
            </label>

            {selectedSpot && (
              <>
                <div className="spot-detail">
                  <h3>{spotIcon(selectedSpot.spot_type)} {selectedSpot.name}</h3>
                  <p className="spot-meta">
                    <span className="tag">{selectedSpot.spot_type}</span>
                    {selectedSpot.capacity ? <span className="tag">Sleeps {selectedSpot.capacity}</span> : null}
                    <span className="tag">{money(selectedSpot.nightly_cents)}/night</span>
                  </p>
                  {selectedSpot.description && <p className="muted">{selectedSpot.description}</p>}
                </div>

                <h3>Already booked</h3>
                {loadingRes ? (
                  <p className="muted">Loading availability…</p>
                ) : upcomingBookings.length === 0 && upcomingBlocks.length === 0 ? (
                  <p className="notice">Wide open — no upcoming bookings for this spot.</p>
                ) : (
                  <>
                    <p className="muted">These date ranges are taken. Any dates outside them are available.</p>
                    <ul className="booked-list">
                      {upcomingBookings.map((r) => {
                        const n = nightsBetween(r.start_date, r.end_date);
                        return (
                          <li key={r.id}>
                            <span className="booked-range">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                            <span className="booked-nights">{n} {n === 1 ? 'night' : 'nights'}</span>
                          </li>
                        );
                      })}
                      {upcomingBlocks.map((b) => (
                        <li key={b.id} className="blocked-row">
                          <span className="booked-range">{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</span>
                          <span className="blocked-tag">Club {b.reason || 'hold'}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <form className="booking-form" onSubmit={handleSubmit}>
                  <div className="field">
                    <span>Pick your dates</span>
                    <p className="muted booking-cal-hint">
                      Tap a check-in day, then a check-out day. Shaded days are already booked.
                    </p>
                    <DayPicker
                      mode="range"
                      selected={range}
                      onSelect={handleRangeSelect}
                      disabled={disabledDays}
                      excludeDisabled
                      modifiers={{ booked: bookedRanges, blocked: blockedRanges }}
                      modifiersClassNames={{ booked: 'rdp-day-booked', blocked: 'rdp-day-blocked' }}
                      numberOfMonths={1}
                    />
                  </div>
                  <p className="booking-selection">
                    {form.startDate && form.endDate ? (
                      <>
                        <strong>{fmtDate(form.startDate)}</strong> → <strong>{fmtDate(form.endDate)}</strong>
                        {' '}· {nightsBetween(form.startDate, form.endDate)}{' '}
                        {nightsBetween(form.startDate, form.endDate) === 1 ? 'night' : 'nights'}
                      </>
                    ) : (
                      <span className="muted">No dates selected yet.</span>
                    )}
                  </p>
                  <button className="primary" type="submit" disabled={submitting || !form.startDate || !form.endDate}>
                    {submitting ? 'Booking…' : 'Book it'}
                  </button>
                </form>

                {feedback && (
                  <p className={feedback.kind === 'ok' ? 'notice' : 'error-inline'}>{feedback.message}</p>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="card">
        <h2>Your reservations</h2>
        {loadingMine ? (
          <p className="muted">Loading your bookings…</p>
        ) : mine.length === 0 ? (
          <p className="muted">You have no reservations yet. Book a spot above to get started.</p>
        ) : (
          <>
            {myUpcoming.length > 0 && (
              <>
                <h3>Upcoming</h3>
                <ul className="my-res-list">
                  {myUpcoming.map((r) => <MyReservationCard key={r.id} r={r} />)}
                </ul>
              </>
            )}
            {myPast.length > 0 && (
              <>
                <h3 className="muted-heading">Past stays</h3>
                <ul className="my-res-list my-res-past">
                  {myPast.map((r) => <MyReservationCard key={r.id} r={r} past />)}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

function fmtShortDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso).slice(0, 10);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function CabinNightsBanner({ usage }) {
  const { allowance = 0, used = 0, remaining = 0, yearEnd } = usage;
  const pct = allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0;
  const low = remaining <= 3 && remaining > 0;
  const none = remaining <= 0;
  return (
    <div className={`card nights-banner${none ? ' nights-banner-empty' : low ? ' nights-banner-low' : ''}`}>
      <h2>🏡 Cabin nights this year</h2>
      <p className="nights-summary">
        <strong>{remaining}</strong> of <strong>{allowance}</strong> nights remaining
        {' '}· {used} used
      </p>
      <div className="nights-bar" role="progressbar" aria-valuenow={used} aria-valuemin={0} aria-valuemax={allowance}>
        <span className="nights-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="muted nights-reset">
        Shared across your household{yearEnd ? ` · resets ${fmtShortDate(yearEnd)}` : ''}
      </p>
      {none && <p className="error-inline">You've used all your cabin nights for this year.</p>}
      {low && <p className="notice">Heads up — only {remaining} cabin {remaining === 1 ? 'night' : 'nights'} left this year.</p>}
    </div>
  );
}

function MyReservationCard({ r, past }) {
  const n = nightsBetween(r.start_date, r.end_date);
  const total = n * (r.nightly_cents || 0);
  return (
    <li className={`my-res-card${past ? ' is-past' : ''}`}>
      <div className="my-res-head">
        <span className="my-res-title">{spotIcon(r.spot_type)} {r.spot_name}</span>
        <span className="tag">{r.spot_type}</span>
      </div>
      <div className="my-res-dates">
        {fmtDate(r.start_date)} <span className="arrow">→</span> {fmtDate(r.end_date)}
      </div>
      <div className="my-res-meta">
        <span>{n} {n === 1 ? 'night' : 'nights'}</span>
        {r.capacity ? <span>· Sleeps {r.capacity}</span> : null}
        <span>· {money(r.nightly_cents)}/night</span>
        {total > 0 ? <span>· {money(total)} total</span> : null}
      </div>
      {r.spot_description && <p className="my-res-desc muted">{r.spot_description}</p>}
    </li>
  );
}
